import { Component, OnInit, inject, ChangeDetectionStrategy, input, ChangeDetectorRef } from '@angular/core';
import { Router, RouterLink } from '@angular/router';

import { ContractDetailsService } from 'src/app/shared/services/contract-details.service';
import { AccordianStateInterface, EntPrimaryTableConfigInterface, BRERequirement, ResponseStatusInterface } from 'src/app/shared/models/app.model';
import { RequirementsTableConfig } from './requirement-table-config';
import { Message, ResponseStatus } from 'src/config/constants';
import { AnalyticsAppService } from 'src/app/shared/services/analytics.service';
import { FileMetadataService } from 'src/app/shared/services/file-metadata.service';

import { EntFormWidgetsModule } from '@ng/ent-components/ent-form-widgets';
import { EntAccordionModule } from '@ng/ent-components/ent-accordion';
import { EntButtonTrayModule } from '@ng/ent-components/ent-button-tray';
import { EntTablesModule } from '@ng/ent-components/ent-tables';


@Component({
    selector: 'app-requirement',
    templateUrl: './requirement.component.html',
    imports: [EntFormWidgetsModule, EntAccordionModule, EntButtonTrayModule, RouterLink, EntTablesModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RequirementComponent implements OnInit{
  private readonly router = inject(Router);
  private readonly contractDetailsService = inject(ContractDetailsService);
  private readonly analytics = inject(AnalyticsAppService);
  private readonly fileMetadataService = inject(FileMetadataService);
  private readonly cdr = inject(ChangeDetectorRef);


  /** Requirements displayed in the outstanding and completed requirement tables. */
  readonly requirementData = input<BRERequirement[]>([]);

  completedTableConfig: EntPrimaryTableConfigInterface = JSON.parse(JSON.stringify(RequirementsTableConfig));
  outstandingTableConfig: EntPrimaryTableConfigInterface = JSON.parse(JSON.stringify(RequirementsTableConfig));

  accordianState: AccordianStateInterface = {opened: false};
  message = Message;
  showInfoMessage: boolean = false;
  showWarningMessage: boolean = false;
  contractNumber!: number
  restrictChaseUser = false;
  showPotentialOutstandingMsg = false;
  filesToDownload: any;
  showDownloadWarningMessage: any;
  fileMetadata: any;
  requirementsWithDownload: Set<any> = new Set<any>();
  responseStatus: ResponseStatusInterface = ResponseStatus;

  ngOnInit(): void {
    // Clear previous data
    this.completedTableConfig.data = [];
    this.outstandingTableConfig.data = [];
    this.showInfoMessage = false;
    this.showWarningMessage = false;
    
    this.loadRequirementData();
    this.completedTableConfig.column = this.completedTableConfig.column?.filter(col => col.columnKey != 'Action');
    const onlyInfoRequirements = this.requirementData()?.filter((data: any) => {
      return data.RequirementSeverity === 'NIGO' && data.RequirementStatus === 'Complete';
    }).length > 0;
    if (!onlyInfoRequirements) {
      this.completedTableConfig.column = this.completedTableConfig.column?.filter(col => col.columnKey != 'Comment');
    }
    const isNIGORequirements = this.requirementData()?.filter((data: any) => {
      return data.RequirementSeverity === 'NIGO' && data.RequirementStatus === 'Open';
    }).length > 0;
    if (!isNIGORequirements) {
      this.outstandingTableConfig.column = this.outstandingTableConfig.column?.filter(col => col.columnKey != 'Comment');
    }
    this.restrictChaseUsers();
    this.potentialOutStandingMsgCheck();
    this.completedTableConfig.config.tableClass = 'completed-requirements-table';
    this.contractDetailsService.licensingViewLinkClick.subscribe(resp => {
      this.accordianState.opened = !!resp;
      this.cdr.markForCheck();
    });
    this.contractNumber = this.contractDetailsService.getContractNumber();
    this.fileMetadataService.fetchFileMetadata().subscribe((res: any)=>{
      if (res === this.responseStatus.success) {
        this.requirementsWithDownload = this.fileMetadataService.getRequirementsWithDownload();
        this.showDownloadWarningMessage = this.outstandingTableConfig.data
          .some((r: any) => this.requirementsWithDownload.has(r.ID));
      }
      this.cdr.markForCheck();
    })
  }

  /**
   * check for the pure CHASE users and block outstanding requirements page
   */
  restrictChaseUsers(): void {
    // restrict JPMC users
    this.restrictChaseUser = true;
    const strategicPartner = this.getSessionArray('STRATEGIC_PARTNER');
    const brokerDealerAffiliation = this.getSessionArray('BROKER_DEALER_AFFILIATION');
    const allTinsFirmNames = this.getSessionArray('ALL_TINS_FIRMNAMES');
    if ((this.checkForChaseValue(strategicPartner) || this.checkForChaseValue(brokerDealerAffiliation) || this.checkForChaseValue(allTinsFirmNames)) ||
        (strategicPartner?.length === 0 && brokerDealerAffiliation?.length === 0 && allTinsFirmNames?.length === 0)) {
      this.restrictChaseUser = false;
    }
    if (this.restrictChaseUser) {
      this.outstandingTableConfig.column = this.outstandingTableConfig.column?.filter(col => col.columnKey != 'Action' && col.columnKey != 'Comment');
      this.outstandingTableConfig.config.tableClass = 'outstanding-requirements-table hide-col';
      this.completedTableConfig.column = this.completedTableConfig.column?.filter(col => col.columnKey != 'Comment');
    }
  }

  private getSessionArray(key: string): string[] {
    const rawValue = sessionStorage.getItem(key);
    if (!rawValue) {
      return [];
    }
    try {
      const parsedValue = JSON.parse(rawValue);
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch {
      return [];
    }
  }

  /**
   * check for CHASE value and return the flag
   * @param data - strategicPartner, brokerDealerAffiliation, allTinsFirmNames values from UPS response
   * @returns boolean value
   */
  checkForChaseValue(data: any): boolean {
    let restrictChaseUser = false;
    data?.forEach((element: string) => {
      if (element?.toLowerCase() !== 'chase') {
        restrictChaseUser = true;
      }
    });
    return restrictChaseUser;
  }

  /**
   * Saves the 'opened' state of the outstanding requirements accordian to the accordianState property
   * @param event stateChange event fired by the accordian component
   */
  stateChange(event: any) {
    if (event.elRef.nativeElement.classList.contains('outstanding-requirements')) {
      this.accordianState.opened = event.opened;
    }
  }

  /**
   * Sorts the requirements data passed from the parent component into the outstanding and completed tables
   * Sets visibiity of warning / info messages for outstanding requirements
   */
  loadRequirementData(): void {
    this.requirementData()?.forEach(r => {
      if (r.RequirementStatus?.toLowerCase() === 'open') {
        this.outstandingTableConfig.data.push(r);
        if (r.RequirementSeverity === "INFO"){
          this.showInfoMessage = true;
        } else{
          this.showWarningMessage = true;
        }
      } else {
        this.completedTableConfig.data.push(r);
      }
    });
  }

  /**
   * Navigates to /requirements page when hyperlinks in the table are clicked
   * @param e tableCellClick event fired by the table component
   */
  tableCellClick(dataObj: any): void {
    if (dataObj.data?.Action === 'RESOLVE' || dataObj.colKey === 'Comment') {
      this.router.navigate(['/requirements', this.contractNumber], { fragment: `form-${dataObj.data?.ID}` });
      const clickedText = dataObj?.e?.srcElement?.innerText;
      this.analytics.logAnalytics({
        event_action: 'link',
        event_name: this.getEventName(clickedText, dataObj),
        event_type: 'pending tool - annuity',
        event_status: 'outstanding requirements'
      })
    }
  }

  /**
   * get event name for analytics
   * @param clickedText from table col
    * @param e table cell-click event
   * @returns string
   */
  getEventName(clickedText: string, _e: any): string {
   if (clickedText === 'RESOLVE') {
    return 'resolve requirement';
   }
   if (clickedText === 'REVIEW') {
    return 'review requirement';
   }
   return 'view comments';
  }

  /**
   * trigger analytics for resolve all requirement button click
   */
  triggerAnalytics(): void {
    this.analytics.logAnalytics({
        event_action: 'button',
        event_name: 'resolve all requirements',
        event_type: 'pending tool - annuity',
        event_status: 'outstanding requirements'
    })
  }

  /**
   * check contract issued or not and no outstanding data and set flag showPotentialOutstandingMsg
   */
  potentialOutStandingMsgCheck(): void {
    const trackerStatus = this.contractDetailsService.getContractDetails()?.ContractStatusTracker;
    const contractIssued = this.isContractIssued(trackerStatus);
    const noOutstandingData = this.outstandingTableConfig.data.length === 0;
    this.showPotentialOutstandingMsg = !contractIssued && noOutstandingData;
  }

  /**
   * check contract issued or not
   */
  private isContractIssued(trackerStatus: any[]): boolean {
    return trackerStatus?.some((data: any) => data.TrackerChevron === 'Contract issued' && data.ChevronStatus === 'Completed' );
  }

}
